import { ArrowRight, History, RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shouldUseMock } from '../../api/config';
import {
  ContextAskButton,
  type AeternaLoopContextValue,
} from '../../components/aeterna-loop';
import { PageHeader } from '../../components/PageHeader';
import { showToast } from '../../components/Toast';
import { executeApi, type LongevityDayPackage } from '../../features/execute/api';
import { getPlanGoalCards } from '../../features/execute/longevity';
import type {
  AbilityLedgerResponse,
  CycleReviewResponse,
  LongevityPracticeSummary,
  PracticeTask,
  WeeklyPracticePlan,
  WorkoutSessionSummary,
} from '../../features/execute/longevity';
import { useAppleHealth } from '../../hooks/useAppleHealth';
import { TimeBadge } from './LongevityShared';
import {
  getCachedLongevityHome,
  setCachedLongevityHome,
} from './longevitySessionCache';
import styles from './LongevityPages.module.css';

const HOME_LOAD_TIMEOUT_MS = 10_000;

function practiceStatusText(status: string) {
  const map: Record<string, string> = {
    on_track: '达标',
    building: '累积中',
    missing: '缺数据',
    observed: '已观察',
    ready: '可继续',
    watch: '观察',
    downshift: '降级',
    maintain: '保持',
    hold: '保持',
    data_gap: '缺数据',
    unknown: '未知',
  };
  return map[status] ?? status;
}

function providerStatusText(status: string) {
  const map: Record<string, string> = {
    synced: '已同步',
    not_connected: '未连接',
    ready_for_connector: '待接入',
    active: '记录中',
    available: '可记录',
  };
  return map[status] ?? status;
}

function formatShortDateTime(value?: string | null) {
  if (!value) return '未知时间';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '未知时间';
  return date.toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function workoutMetricLine(workout: WorkoutSessionSummary) {
  const parts = [`${workout.durationMinutes} 分钟`];
  if (workout.distanceKilometers) parts.push(`${workout.distanceKilometers} km`);
  if (workout.energyKilocalories) parts.push(`${workout.energyKilocalories} kcal`);
  return parts.join(' · ');
}

function abilityStatusLabel(status: string) {
  return practiceStatusText(status);
}

function isWeeklyTrainingPillar(label: string) {
  return !/睡眠|恢复/u.test(label);
}

function isMissingPillarData(pillar: LongevityPracticeSummary['pillars'][number]) {
  return pillar.status === 'missing' || pillar.status === 'unknown';
}

function compactUnit(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, string> = {
    zone2: '分钟',
    zone5: '次',
    strength: '次',
    stability: '天',
    recovery: '分钟/晚',
  };
  return map[pillar.key] ?? pillar.unit;
}

function pillarSourceText(source: string) {
  const map: Record<string, string> = {
    wearable_workouts: '手表运动记录估算',
    wearable_or_manual: '手表记录 + 手动确认',
    manual_feedback: '手动反馈',
    wearable_sleep: '睡眠样本',
    wearable_steps: '步数趋势',
    exercise_time: '运动记录',
    manual_or_missing: '手动记录',
  };
  return map[source] ?? source;
}

function weekDataTitle(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, string> = {
    zone2: 'Zone 2 低强度有氧',
    zone5: 'Zone 5 高强度训练',
    strength: '力量练习',
    stability: '稳定性练习',
    recovery: '恢复观察',
  };
  return map[pillar.key] ?? pillar.label;
}

function weekDataMeaning(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, string> = {
    zone2: '能持续、还能说话的有氧运动',
    zone5: '短时间高强度刺激，先确认再做',
    strength: '围绕肌力和日常动作能力',
    stability: '平衡、核心和防跌倒能力',
    recovery: '用睡眠趋势决定当前是否加量',
  };
  return map[pillar.key] ?? pillarSourceText(pillar.source);
}

function weekDataTarget(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, number> = {
    zone2: 150,
    strength: 2,
    stability: 3,
  };
  return map[pillar.key] ?? null;
}

function weekDataProgress(pillar: LongevityPracticeSummary['pillars'][number]) {
  if (isMissingPillarData(pillar)) {
    return {
      primary: pillar.key === 'zone5' ? '暂不安排' : '暂未同步',
      secondary: pillar.key === 'zone5' ? '先等恢复和数据更稳定' : '同步或手动记录后再判断',
    };
  }

  if (pillar.key === 'recovery') {
    return {
      primary: `近 7 天约 ${pillar.value} ${compactUnit(pillar)}`,
      secondary: '只用于决定当前是否降级',
    };
  }

  const target = weekDataTarget(pillar);
  if (target !== null) {
    const remaining = Math.max(target - pillar.value, 0);
    return {
      primary: `已完成 ${pillar.value} / ${target} ${compactUnit(pillar)}`,
      secondary: remaining > 0 ? `还差 ${remaining} ${compactUnit(pillar)}` : '本周目标已够用',
    };
  }

  return {
    primary: `已记录 ${pillar.value} ${compactUnit(pillar)}`,
    secondary: pillar.targetLabel,
  };
}

function weekDataAction(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, string> = {
    zone2: '当前可以用走路、骑车或椭圆机补一点。',
    zone5: '先不主动加高强度；需要个体化确认。',
    strength: '这周再补一次轻量力量练习。',
    stability: '当前可以做 5-10 分钟平衡或核心。',
    recovery: '恢复偏弱时，当前做低强度版本即可。',
  };
  return map[pillar.key] ?? pillar.targetLabel;
}

function weeklyDecisionText(summary: LongevityPracticeSummary) {
  if (summary.recovery.status === 'downshift') return '当前先降级，不加量。';
  if (summary.recovery.status === 'watch') return '本周先维持，观察恢复。';
  if (summary.recovery.status === 'ready') return '本周可以继续累积。';
  return '先补齐数据，再做判断。';
}

function recoveryDecisionCopy(summary: LongevityPracticeSummary) {
  const core = summary.recovery.summary
    .replace(/；?只用于调节实践强度。?/gu, '')
    .replace(/，?不替代医学诊断。?/gu, '')
    .replace(/。$/u, '');
  return `${core}；只用于调节实践强度，不替代医学诊断。`;
}

function weeklyPlanCopy(pillar: LongevityPracticeSummary['pillars'][number]) {
  const map: Record<string, { title: string; detail: string }> = {
    zone2: {
      title: 'Zone 2 低强度有氧',
      detail: '这周先累计到 150 分钟附近，走路、骑车都可以。',
    },
    zone5: {
      title: 'Zone 5 高强度训练',
      detail: '先不硬加量；等医生/教练或更多数据确认后再安排。',
    },
    strength: {
      title: '力量练习',
      detail: '这周先完成 2 次，动作轻一点也算。',
    },
    stability: {
      title: '稳定性练习',
      detail: '这周先练 3 天，优先防跌倒和平衡。',
    },
  };
  return map[pillar.key] ?? { title: pillar.label, detail: pillar.targetLabel };
}

function userFacingWeeklyTitle(title: string) {
  return title
    .replace(/^第\s*\d+\s*-\s*\d+\s*天[:：]\s*/u, '本轮：')
    .replace(/^本周计划[:：]\s*/u, '本轮：');
}

function buildWeeklyPracticeAskPrompt(
  weeklyPlan: WeeklyPracticePlan,
  task: PracticeTask,
) {
  const decision = weeklyPlan.plannerDecision;
  const today = weeklyPlan.days.find((day) => day.taskId === task.id) ?? weeklyPlan.days[0];
  const allowed = decision.allowedActions.slice(0, 3).join('、') || '记录完成量和身体反馈';
  const blocked = decision.blockedActions.slice(0, 3).join('、') || '不自动加量';
  const stopRule = weeklyPlan.safetyBoundary.stopRules[0] ?? task.safetyStop;
  return [
    `我的 28 天实践方案是「${weeklyPlan.title}」。`,
    `后端 weekly-plan contract 的判断是「${decision.label}」：${decision.rationale}`,
    `当前任务是「${task.title}」，剂量是「${task.doseLabel}」。当前 planner note：${today?.plannerNote ?? '按轻量剂量完成，记录完成量、疲劳感和疼痛。'}`,
    `本轮允许的动作边界：${allowed}。本轮不要做：${blocked}。`,
    `安全停止线：${stopRule}`,
    '请用用户能听懂的话解释为什么当前这样排、做到什么程度就够、完成后要观察哪些反馈；不要替代医生诊断或治疗建议。',
  ].join('\n');
}

function withHomeLoadTimeout<T>(promise: Promise<T>, timeoutMessage: string): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), HOME_LOAD_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  });
}

export default function LongevityHomePage() {
  const navigate = useNavigate();
  const [initialCache] = useState(() => getCachedLongevityHome());
  const [dayPackage, setDayPackage] = useState<LongevityDayPackage | null>(initialCache ?? null);
  const [isLoading, setIsLoading] = useState(initialCache === undefined);
  const [isRefreshing, setIsRefreshing] = useState(initialCache !== undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [practiceSummary, setPracticeSummary] = useState<LongevityPracticeSummary | null>(null);
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPracticePlan | null>(null);
  const [abilityLedger, setAbilityLedger] = useState<AbilityLedgerResponse | null>(null);
  const [recentWorkouts, setRecentWorkouts] = useState<WorkoutSessionSummary[]>([]);
  const [cycleReview, setCycleReview] = useState<CycleReviewResponse | null>(null);
  const [isSummaryRefreshing, setIsSummaryRefreshing] = useState(false);
  const appleHealth = useAppleHealth();

  const loadWeeklyPlan = useCallback(async (planId: string) => {
    try {
      const plan = await executeApi.getLongevityWeeklyPlan(planId);
      setWeeklyPlan(plan);
      return plan;
    } catch {
      setWeeklyPlan(null);
      return null;
    }
  }, []);

  const loadPracticeSummary = useCallback(async () => {
    setIsSummaryRefreshing(true);
    try {
      const [summary, ledger, workouts] = await Promise.all([
        executeApi.getLongevityPracticeSummary(),
        executeApi.getLongevityAbilityLedger().catch(() => null),
        executeApi.getLongevityWorkouts().catch(() => []),
      ]);
      setPracticeSummary(summary);
      setAbilityLedger(ledger);
      setRecentWorkouts(workouts);
    } catch {
      setPracticeSummary(null);
      setAbilityLedger(null);
      setRecentWorkouts([]);
    } finally {
      setIsSummaryRefreshing(false);
    }
  }, []);

  const loadCycleReview = useCallback(async (planId: string) => {
    try {
      const review = await executeApi.getLongevityCycleReview(planId);
      setCycleReview(review);
      return review;
    } catch {
      setCycleReview(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadActivePlan(silent: boolean) {
      if (silent) setIsRefreshing(true);
      else setIsLoading(true);
      setLoadError(null);
      try {
        const plan = await withHomeLoadTimeout(
          executeApi.getActiveLongevityPlan(),
          '当前计划暂时没有读回来，请稍后重新读取。',
        );
        if (!alive) return;
        if (!plan) {
          setDayPackage(null);
          setWeeklyPlan(null);
          setCachedLongevityHome(null);
          return;
        }
        const currentDay = plan.currentDay || 1;
        const [nextPackage] = await Promise.all([
          withHomeLoadTimeout(
            executeApi.getLongevityDay(plan.id, currentDay),
            '当前的一件小事暂时没有同步回来，请稍后重新读取。',
          ),
          loadWeeklyPlan(plan.id),
          loadCycleReview(plan.id),
        ]);
        if (alive) {
          setDayPackage(nextPackage);
          setCachedLongevityHome(nextPackage);
        }
      } catch (error) {
        if (alive) setLoadError(error instanceof Error ? error.message : '百岁实践加载失败');
      } finally {
        if (alive) setIsLoading(false);
        if (alive) setIsRefreshing(false);
      }
    }
    loadActivePlan(initialCache !== undefined && reloadKey === 0);
    return () => {
      alive = false;
    };
  }, [initialCache, loadCycleReview, loadWeeklyPlan, reloadKey]);

  useEffect(() => {
    loadPracticeSummary();
  }, [loadPracticeSummary, reloadKey]);

  const refreshPracticeData = async () => {
    try {
      if (appleHealth.isAvailable) {
        if (appleHealth.isAuthorized) {
          await appleHealth.refresh();
        } else {
          await appleHealth.requestPermission();
        }
      }
      await Promise.all([
        loadPracticeSummary(),
        dayPackage ? loadWeeklyPlan(dayPackage.plan.id) : Promise.resolve(null),
        dayPackage ? loadCycleReview(dayPackage.plan.id) : Promise.resolve(null),
      ]);
      showToast('实践数据已刷新');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '实践数据刷新失败');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader mode="primary" title="践行" subtitle="从低风险的小事开始" showProfile />
        <main className={styles.content}>
          <section className={styles.hero} aria-busy="true">
            <div>
              <p className={styles.eyebrow}>我的百岁实践</p>
              <h1 className={styles.heroTitle}>正在准备当前的一件小事。</h1>
              <p className={styles.heroText}>先读取当前计划，再同步实践和真知材料。</p>
            </div>
            <div className={styles.skeletonTrack} aria-hidden />
            <div className={styles.heroActions}>
              <div className={styles.skeletonButton} aria-hidden />
              <div className={styles.skeletonButtonSecondary} aria-hidden />
            </div>
          </section>
          <section className={styles.section} aria-busy="true">
            <div className={styles.skeletonLineShort} aria-hidden />
            <div className={styles.skeletonGoalGrid} aria-hidden>
              <div />
              <div />
              <div />
            </div>
          </section>
          <section className={styles.section} aria-busy="true">
            <div className={styles.dailyAction}>
              <div className={styles.skeletonLineShort} aria-hidden />
              <div className={styles.skeletonLineWide} aria-hidden />
              <div className={styles.skeletonLineMid} aria-hidden />
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (!dayPackage) {
    const hasLoadFailure = Boolean(loadError);

    return (
      <div className={styles.page}>
        <PageHeader mode="primary" title="践行" subtitle="从低风险的小事开始" showProfile />
        <main className={styles.content}>
          <section className={styles.hero}>
            <div>
              <p className={styles.eyebrow}>{hasLoadFailure ? '读取失败' : '百岁实践'}</p>
              <h1 className={styles.heroTitle}>
                {hasLoadFailure ? '当前实践暂时没读出来。' : '先创建一个真实实践，再开始当前的一件事。'}
              </h1>
              <p className={styles.heroText}>
                {loadError ?? '首次只需要 3 张主目标卡和一个当前阻力。'}
              </p>
            </div>
            <div className={styles.heroActions}>
              {hasLoadFailure ? (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setReloadKey((key) => key + 1)}
                >
                  重新读取
                  <RotateCcw size={16} strokeWidth={1.5} aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => navigate('/execute/longevity/create')}
                >
                  创建百岁实践
                  <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
                </button>
              )}
              {hasLoadFailure && (
                <button type="button" className={styles.secondaryButton} onClick={() => navigate('/knowledge')}>
                  先去真知
                  <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
                </button>
              )}
            </div>
          </section>
        </main>
      </div>
    );
  }

  const plan = dayPackage.plan;
  const brief = dayPackage.dailyBrief;
  const goals = getPlanGoalCards(plan);
  const task = brief.practiceTask;
  const currentDay = plan.currentDay || dayPackage.dayNumber || 1;
  const isMockPracticeData = shouldUseMock('execute');
  const practiceSummaryCopy = isMockPracticeData
    ? '示例数据；正式环境会从 Apple Health、Garmin 或手动记录同步。'
    : practiceSummary?.summary ?? '用于调整实践节奏，不替代医学诊断。';
  const fallbackWeeklyPlanItems = practiceSummary?.pillars.filter((pillar) => isWeeklyTrainingPillar(pillar.label)).slice(0, 4) ?? [];
  const abilityPreview = abilityLedger?.goals
    .flatMap((goal) => goal.domains.map((domain) => ({ ...domain, goalTitle: goal.title })))
    .slice(0, 4) ?? [];
  const latestWorkouts = recentWorkouts.slice(0, 3);
  const weeklyPlanTitle = userFacingWeeklyTitle(weeklyPlan?.title ?? '本轮安排');
  const weeklyPlanSummary = weeklyPlan?.plannerDecision.rationale ?? '不用懂训练术语，只看当前要完成的一件事。';
  const weeklyPlanItems = weeklyPlan?.days ?? [];
  const currentWeeklyItem = weeklyPlanItems.find((day) => day.taskId === task.id);
  const actionBundle = brief.actionBundle ?? currentWeeklyItem?.actionBundle ?? null;
  const nextPlanItems = weeklyPlanItems
    .filter((day) => day.taskId !== task.id)
    .slice(0, 2);
  const fallbackPreviewItems = fallbackWeeklyPlanItems.slice(0, 2);
  const currentActionPath = `/execute/longevity/day/${currentDay}`;
  const primaryGoalTitle = goals[0]?.title ?? '你的百岁目标';
  const manualFeedbackCopy = '完成量、吃力程度、疼痛';
  const deviceStatusCopy = appleHealth.isAvailable
    ? appleHealth.connectionStatus.label
    : '可用手动记录';
  const weeklyPracticeBoundary = weeklyPlan
    ? `本轮生成规则：${weeklyPlan.plannerDecision.label}；${weeklyPlan.evidenceBoundary.doctorBoundary}`
    : '来自当前百岁目标、当前任务和本周实践数据；用于调节实践节奏，不替代医学诊断。';
  const practiceContext: AeternaLoopContextValue = {
    sourceType: 'practice',
    sourceId: `${plan.id}:${currentDay}`,
    sourceTitle: `当前实践：${task.title}`,
    stage: 'act',
    evidenceBoundary: weeklyPracticeBoundary,
    askPrompt: weeklyPlan
      ? buildWeeklyPracticeAskPrompt(weeklyPlan, task)
      : `我的当前实践是「${task.title}」，剂量是「${task.doseLabel}」。请结合我的百岁目标解释为什么这样安排，并告诉我当前要观察哪些反馈。`,
  };

  return (
    <div className={styles.page}>
      <PageHeader mode="primary" title="践行" subtitle="从低风险的小事开始" showProfile />

      <main className={styles.content}>
        <section className={`${styles.hero} ${styles.currentActionHero}`}>
          <div className={styles.currentActionPanel}>
            <div className={styles.currentActionHeader}>
              <p className={styles.eyebrow}>当前实践</p>
              <TimeBadge label={`${task.estimatedMinutes} 分钟`} />
            </div>
            <div>
              <h1 className={styles.currentActionTitle}>{task.title}</h1>
              <p className={styles.currentActionDose}>{task.doseLabel}</p>
            </div>
            {actionBundle && (
              <div className={styles.actionBundlePanel}>
                <div className={styles.actionBundleHeader}>
                  <span className={styles.domainBadge}>{actionBundle.domainLabel}</span>
                  <span className={styles.taskMeta}>{actionBundle.completionMetric}</span>
                </div>
                <p className={styles.cardText}>{actionBundle.observation.prompt}</p>
                <div className={styles.doseOptionRow}>
                  {actionBundle.doseOptions.slice(0, 3).map((option) => (
                    <span className={styles.doseOptionPill} key={option.level}>
                      {option.label} · {option.doseLabel}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className={styles.currentActionReason}>
              这次只围绕“{primaryGoalTitle}”推进一小步。完成后记录实际做了多少、感觉有多累、有没有疼。
            </p>
            <p className={styles.currentActionSafety}>安全线：{task.safetyStop}</p>
            {isRefreshing && <p className={styles.boundaryDescription}>正在更新最新状态。</p>}
            <div className={styles.currentActionActions}>
              <button className={styles.primaryButton} onClick={() => navigate(currentActionPath, { state: { dayPackage } })}>
                记录这次
                <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
              </button>
              <button className={styles.secondaryButton} onClick={() => navigate(currentActionPath, { state: { dayPackage } })}>
                查看动作
              </button>
              <ContextAskButton
                className={styles.secondaryButton}
                context={practiceContext}
                label="问为什么"
                onNavigate={navigate}
              />
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.goalSummarySection}`}>
          <div className={styles.goalSummaryHeader}>
            <p className={styles.eyebrow}>百岁目标</p>
            <div className={styles.goalSummaryActions}>
              <button className={styles.quietButton} onClick={() => navigate('/execute/longevity/goals')}>
                愿景墙
              </button>
              <button className={styles.quietButton} onClick={() => navigate('/execute/longevity/history')}>
                <History size={16} strokeWidth={1.5} aria-hidden />
                历史
              </button>
            </div>
          </div>
          <div className={styles.goalChipGrid}>
            {goals.map((goal, index) => (
              <article className={styles.goalChip} key={goal.id}>
                <span className={styles.goalMiniIndex}>{index + 1}</span>
                <span>{goal.title}</span>
              </article>
            ))}
          </div>
        </section>

        <div className={styles.stack}>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div>
                <div className={styles.sectionTitleRow}>
                  <h2 className={styles.sectionTitle}>调节依据</h2>
                  {isMockPracticeData && <span className={styles.dataModeBadge}>示例数据</span>}
                </div>
                <p className={styles.sectionSubtitle}>{practiceSummaryCopy}</p>
              </div>
              <button
                className={styles.iconTextButton}
                onClick={refreshPracticeData}
                disabled={isSummaryRefreshing || appleHealth.loading || appleHealth.isSyncingToCloud}
              >
                <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
                刷新
              </button>
            </div>

            {practiceSummary ? (
              <div className={styles.weekProgressBody}>
                <article className={styles.weekDecisionCard}>
                  <div>
                    <p className={styles.eyebrow}>本轮判断</p>
                    <h3 className={styles.weekDecisionTitle}>{weeklyDecisionText(practiceSummary)}</h3>
                    <p className={styles.weekDecisionText}>
                      {recoveryDecisionCopy(practiceSummary)}
                    </p>
                  </div>
                  <span className={styles.weekDecisionStatus}>{practiceSummary.recovery.label}</span>
                </article>

                <div className={styles.dataSnapshotGrid}>
                  <article className={styles.dataSnapshotCard}>
                    <p className={styles.eyebrow}>手动反馈</p>
                    <h3 className={styles.cardTitle}>{manualFeedbackCopy}</h3>
                    <p>没有智能设备也能先跑闭环；完成后记录这三项，下一轮就能调节。</p>
                  </article>
                  <article className={styles.dataSnapshotCard}>
                    <p className={styles.eyebrow}>设备状态</p>
                    <h3 className={styles.cardTitle}>{deviceStatusCopy}</h3>
                    <p>{appleHealth.connectionStatus.note}</p>
                  </article>
                </div>

                <div className={styles.providerStatusStrip}>
                  <div className={styles.providerStatusItem} key="local-apple-health">
                    <span>本机 Apple Health</span>
                    <strong>{appleHealth.connectionStatus.label}</strong>
                  </div>
                  {practiceSummary.providerStatuses.map((provider) => (
                    <div className={styles.providerStatusItem} key={provider.provider}>
                      <span>{provider.label}</span>
                      <strong>{providerStatusText(provider.status)}</strong>
                    </div>
                  ))}
                </div>

                {(abilityPreview.length > 0 || latestWorkouts.length > 0) && (
                  <div className={styles.practiceEvidenceGrid}>
                    {abilityPreview.length > 0 && (
                      <article className={styles.practiceEvidencePanel}>
                        <div className={styles.evidencePanelHeader}>
                          <div>
                            <p className={styles.eyebrow}>能力账本</p>
                            <h3 className={styles.cardTitle}>目标能力现在有证据了</h3>
                          </div>
                          <button className={styles.quietButton} onClick={() => navigate('/execute/longevity/goals')}>
                            查看
                          </button>
                        </div>
                        <div className={styles.abilityLedgerList}>
                          {abilityPreview.map((domain) => (
                            <div className={styles.abilityLedgerItem} key={`${domain.goalTitle}-${domain.key}`}>
                              <span>{domain.label}</span>
                              <strong>{abilityStatusLabel(domain.status)}</strong>
                              <p>{domain.progressLabel}</p>
                            </div>
                          ))}
                        </div>
                      </article>
                    )}

                    {latestWorkouts.length > 0 && (
                      <article className={styles.practiceEvidencePanel}>
                        <div className={styles.evidencePanelHeader}>
                          <div>
                            <p className={styles.eyebrow}>Workout</p>
                            <h3 className={styles.cardTitle}>单次运动可追溯</h3>
                          </div>
                        </div>
                        <div className={styles.workoutList}>
                          {latestWorkouts.map((workout) => (
                            <button
                              className={styles.workoutRow}
                              key={workout.id}
                              onClick={() => navigate(`/execute/longevity/workouts/${encodeURIComponent(workout.id)}`)}
                              type="button"
                            >
                              <span>{workout.activityName}</span>
                              <strong>{workoutMetricLine(workout)}</strong>
                              <small>{formatShortDateTime(workout.endedAt)}</small>
                            </button>
                          ))}
                        </div>
                      </article>
                    )}
                  </div>
                )}

                <details className={styles.inlineDetails}>
                  <summary className={styles.inlineSummary}>查看全部调节依据</summary>
                  <div className={styles.inlineDetailsContent}>
                    <div className={styles.weekSignalList}>
                      {practiceSummary.pillars.map((pillar) => {
                        const isMissing = isMissingPillarData(pillar);
                        const progress = weekDataProgress(pillar);
                        return (
                          <article
                            className={
                              isMissing
                                ? `${styles.weekSignalRow} ${styles.weekSignalRowMissing}`
                                : styles.weekSignalRow
                            }
                            key={pillar.key}
                          >
                            <div className={styles.weekSignalName}>
                              <h3>{weekDataTitle(pillar)}</h3>
                              <p>{weekDataMeaning(pillar)}</p>
                            </div>
                            <div className={styles.weekSignalValue}>
                              <strong>{progress.primary}</strong>
                              <span>{progress.secondary}</span>
                            </div>
                            <div className={styles.weekSignalTarget}>
                              <span>{pillarSourceText(pillar.source)}</span>
                              <p>{weekDataAction(pillar)}</p>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </details>
              </div>
            ) : (
              <article className={styles.infoCard}>
                <p className={styles.eyebrow}>手动闭环</p>
                <h3 className={styles.cardTitle}>先记录完成量、主观吃力程度和疼痛。</h3>
                <p className={styles.cardText}>
                  Apple Health 可在 iOS 授权后同步；Garmin 底座已预留，真实连接需要开发者授权和用户 consent。
                </p>
              </article>
            )}
          </section>

          {cycleReview && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div>
                  <h2 className={styles.sectionTitle}>阶段复盘</h2>
                  <p className={styles.sectionSubtitle}>{cycleReview.summary}</p>
                </div>
              </div>
              <div className={styles.cycleReviewGrid}>
                <article className={styles.dataSnapshotCard}>
                  <p className={styles.eyebrow}>完成反馈</p>
                  <h3 className={styles.cardTitle}>{cycleReview.completedDays}/{cycleReview.windowDays} 天</h3>
                  <p>{cycleReview.keepDoing[0] ?? '继续保留每天一个低摩擦动作。'}</p>
                </article>
                <article className={styles.dataSnapshotCard}>
                  <p className={styles.eyebrow}>运动证据</p>
                  <h3 className={styles.cardTitle}>{cycleReview.workoutCount} 次 workout</h3>
                  <p>{cycleReview.adjustNext[0] ?? '下一轮按缺口调节，不自动加量。'}</p>
                </article>
              </div>
            </section>
          )}

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div>
                <h2 className={styles.sectionTitle}>本轮节奏</h2>
                <p className={styles.sectionSubtitle}>{weeklyPlanSummary}</p>
              </div>
            </div>

            {weeklyPlan && (
              <article className={styles.weekDecisionCard}>
                <div>
                  <p className={styles.eyebrow}>后端生成规则</p>
                  <h3 className={styles.weekDecisionTitle}>{weeklyPlan.plannerDecision.label}</h3>
                  <p className={styles.weekDecisionText}>
                    {weeklyPlan.plannerDecision.rationale}
                  </p>
                </div>
                <span className={styles.weekDecisionStatus}>
                  {practiceStatusText(weeklyPlan.plannerDecision.status)}
                </span>
              </article>
            )}

            <div className={`${styles.weekPlanGrid} ${styles.weekPlanGridCompact}`}>
              {nextPlanItems.length ? nextPlanItems.map((day) => (
                <article className={styles.weekPlanItem} key={day.dayId}>
                  <p className={styles.eyebrow}>
                    接下来 · {day.actionBundle?.domainLabel ?? `${day.estimatedMinutes} 分钟`}
                  </p>
                  <h3 className={styles.cardTitle}>{day.taskTitle}</h3>
                  <p>{day.doseLabel}</p>
                </article>
              )) : fallbackPreviewItems.map((pillar) => {
                const copy = weeklyPlanCopy(pillar);
                return (
                  <article className={styles.weekPlanItem} key={pillar.key}>
                    <p className={styles.eyebrow}>本轮</p>
                    <h3 className={styles.cardTitle}>{copy.title}</h3>
                    <p>{copy.detail}</p>
                  </article>
                );
              })}
            </div>

            {(weeklyPlanItems.length > 0 || fallbackWeeklyPlanItems.length > 0) && (
              <details className={styles.inlineDetails}>
                <summary className={styles.inlineSummary}>查看完整安排</summary>
                <div className={styles.inlineDetailsContent}>
                  <p className={styles.sectionSubtitle}>{weeklyPlanTitle}</p>
                  <div className={styles.weekPlanGrid}>
                    {weeklyPlanItems.length ? weeklyPlanItems.map((day) => (
                      <article className={styles.weekPlanItem} key={day.dayId}>
                        <p className={styles.eyebrow}>
                          行动 {day.dayNumber} · {day.actionBundle?.domainLabel ?? `${day.estimatedMinutes} 分钟`}
                        </p>
                        <h3 className={styles.cardTitle}>{day.taskTitle}</h3>
                        <p>{day.doseLabel} · {day.plannerNote}</p>
                      </article>
                    )) : fallbackWeeklyPlanItems.map((pillar) => {
                      const copy = weeklyPlanCopy(pillar);
                      return (
                        <article className={styles.weekPlanItem} key={pillar.key}>
                          <p className={styles.eyebrow}>本轮</p>
                          <h3 className={styles.cardTitle}>{copy.title}</h3>
                          <p>{copy.detail}</p>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </details>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
